import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import {
  COLOR_PALETTE,
  addCommas,
  allocateRemainingPercentage,
  calculateUnits,
  checkAndRemovePendingTrade,
  containsPre99,
  extractFunds,
  isDateInRange,
  removeFundAndUpdateTotals,
} from "../../helpers/helpers";
import Buttons from "../../atoms/Buttons";
import "./manageinvestments.scss";
import FundSelect from "../FundSelect";
import ExploreAndChangeInvestment from "../ExploreAndChangeInvestments";
import DoughnutChart from "../../atoms/DoughnutChart";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "../../atoms/Icons";
import { ModalOverlay } from "../../atoms/Modal";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../redux/actions";

const ManageInvestment = (props) => {
  const investorObject = props.memberObj?.accountDetailsForSavings?.reduce(
    (youngest, current) =>
      current.retirementAge < youngest.retirementAge ? current : youngest,
  );
  const [memberships] = useState(
    removeFundAndUpdateTotals(checkAndRemovePendingTrade(props.memberships)),
  );
  const [checkIsDirty, setCheckIsDirty] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const isPre99 = containsPre99(props.memberships);

  const [selectFund, setSelectFund] = useState([]);
  const popupShown = useRef(false);
  const errorShown = useRef(false);

  const { fundSwitchFetching, fundSwitchData, fundSwitchError } = useSelector(
    (state) => state.fundSwitch, // eslint-disable-line
  );

  const {
    fundSwitchFutureFetching,
    fundSwitchFutureData,
    fundSwitchFutureError,
  } = useSelector(
    (state) => state.fundSwitchFuture, // eslint-disable-line
  );

  //CMS Integration
  const { data, fetching } = useSelector((state) => state.cmsFundListReducer);

  const dispatch = useDispatch();

  const { getFundList, savingsFundSwitch, savingsFundSwitchFuture } =
    bindActionCreators(
      actionCreators,
      dispatch, // eslint-disable-line
    );

  useEffect(() => {
    getFundList(props.investorId, memberships?.memberships[0]?.membershipId);
  }, []);

  useEffect(() => {
    const items =
      props.investmentStrategyKey === "futurePaymentsOnly"
        ? data?.data?.filter(
            (cmsObject) =>
              !memberships?.memberships[0]?.funds?.some(
                (composerObject) =>
                  composerObject?.investmentId ===
                    cmsObject?.content?.properties?.investmentId &&
                  composerObject?.fundPercentage !== 0, // eslint-disable-line
              ), // eslint-disable-line
          )
        : data?.data?.filter(
            (cmsObject) =>
              !memberships?.memberships[0]?.funds?.some(
                (composerObject) =>
                  composerObject?.investmentId ===
                  cmsObject?.content?.properties?.investmentId, // eslint-disable-line
              ), // eslint-disable-line
          );
    var colorIndex = COLOR_PALETTE.length;
    setSelectFund(
      items?.map((item) => {
        const {
          investmentStyle,
          averageESGRating,
          fundFactsheets,
          fundUse,
          fundObjectiveTruncated,
          composerFundNameTruncated,
          investmentId,
          ...rest
        } = item.content.properties;
        if (
          isDateInRange(
            investorObject?.retirementDate,
            item.content.properties?.fundFrom,
            item.content.properties?.fundTo,
            item.content.properties?.fundName,
            isPre99, // eslint-disable-line
          ) &&
          investmentId !== null
        ) {
          return {
            investmentId: investmentId,
            fundId: composerFundNameTruncated,
            fundType:
              investmentStyle === "Managed for you"
                ? "Managed for me"
                : investmentStyle,
            ESGRating: averageESGRating,
            managedBy: fundUse,
            desc: fundObjectiveTruncated,
            pDFUrl: fundFactsheets ? fundFactsheets[0].url : null,
            color: COLOR_PALETTE[--colorIndex],
            ...rest,
          };
        }
      }), // eslint-disable-line
    );
  }, [fetching, data]);

  //

  const navigate = useNavigate();

  const [addAnotherFund, setAddAnotherFund] = useState(false);

  const [finalRequest, setFinalRequest] = useState([]);

  const [showSummaryPopup, setShowSummaryPopup] = useState(false);

  const [changesConfirmPopup, setChangesConfirmPopup] = useState(false);
  const [initialFunds] = useState(
    extractFunds(memberships, props.investmentStrategyKey)
      .map((fund) => ({
        ...fund,
        fundPercentage:
          props.investmentStrategyKey === "futurePaymentsOnly"
            ? fund.fundPercentage
            : fund.balancePercent,
      }))
      .filter(
        (fund) =>
          !(
            props.investmentStrategyKey === "futurePaymentsOnly" &&
            fund.fundPercentage === 0
          ), // eslint-disable-line
      ), // eslint-disable-line
  );

  const [funds, setFunds] = useState(
    extractFunds(memberships, props.investmentStrategyKey)
      .map((fund) => ({
        ...fund,
        fundPercentage:
          props.investmentStrategyKey === "futurePaymentsOnly"
            ? fund.fundPercentage
            : fund.balancePercent,
      }))
      .filter(
        (fund) =>
          !(
            props.investmentStrategyKey === "futurePaymentsOnly" &&
            fund.fundPercentage === 0
          ), // eslint-disable-line
      ), // eslint-disable-line
  );
  useEffect(() => {
    setFunds([
      ...extractFunds(memberships, props.investmentStrategyKey)
        .map((fund) => ({
          ...fund,
          fundPercentage:
            props.investmentStrategyKey === "futurePaymentsOnly"
              ? fund.fundPercentage
              : fund.balancePercent,
        }))
        .filter(
          (fund) =>
            !(
              props.investmentStrategyKey === "futurePaymentsOnly" &&
              fund.fundPercentage === 0
            ), // eslint-disable-line
        ),
    ]);
  }, []);

  useEffect(() => {
    setCheckIsDirty(
      !initialFunds.every(
        (fund1) =>
          funds.some(
            (fund2) =>
              fund1.investmentId === fund2.investmentId &&
              fund1.fundPercentage === parseFloat(fund2.fundPercentage), // eslint-disable-line
          ), // eslint-disable-line
      ), // eslint-disable-line
    );
  }, [initialFunds, funds]);

  const handleAddAnotherFund = () => {
    setAddAnotherFund(!addAnotherFund);
    setIsHovered(false);
  };

  const handleContinue = () => {
    const newFunds = [...funds]; // Create a copy of the current funds array

    selectFund.forEach((obj) => {
      if (obj?.checked) {
        if (!newFunds.some((item) => item.investmentId === obj?.investmentId)) {
          obj.balance = 0;
          obj.fundPercentage = 0;
          newFunds.push(obj);
        }
      } else {
        const index = newFunds.findIndex(
          (item) => item.investmentId === obj?.investmentId,
        );
        if (index !== -1) {
          newFunds.splice(index, 1); // Remove the object from the array
        }
      }
    });
    setFunds(newFunds); // Update the funds state with the new array
    setAddAnotherFund(!addAnotherFund);
  };

  const handleBack = (event) => {
    event.preventDefault();
    if (addAnotherFund === true) {
      const newFunds = [...funds]; // Create a copy of the current funds array
      selectFund?.forEach((obj) => {
        if (
          obj &&
          !newFunds.some((item) => item?.investmentId === obj?.investmentId)
        ) {
          obj.checked = false;
        }
      });
      setSelectFund(selectFund);
      setAddAnotherFund(!addAnotherFund);
    } else {
      let memberships = props.memberships;
      let investmentStrategy = props.investmentStrategy;
      let investmentStrategyKey = props.investmentStrategyKey;
      let toggle = "savings";
      let combinedState = {
        memberships,
        investmentStrategy,
        investmentStrategyKey,
        toggle,
      };

      navigate("/manage?tab=investments", { state: combinedState });
    }
  };

  const handleCancel = () => {
    navigate("/manage?tab=investments");
  };

  const handleCheckboxChange = (cardId) => {
    setSelectFund((prevCardsData) => {
      return prevCardsData.map((card) => {
        if (card?.investmentId === cardId) {
          return { ...card, checked: !card?.checked };
        }
        return card;
      });
    });
  };

  const handleOnSliderChange = (cardId, value, balance) => {
    setFunds((prevFund) => {
      return prevFund.map((fund) => {
        if (fund.investmentId === cardId) {
          return { ...fund, fundPercentage: value, balance: balance };
        }
        return fund;
      });
    });
  };

  const handleFinalSubmit = () => {
    const initialFundsMap = new Map(
      initialFunds.map((obj) => [obj.investmentId, obj]), // eslint-disable-line
    );
    const combinedArray = funds.map((obj2) => {
      const matchingObj = initialFundsMap.get(obj2.investmentId);
      if (matchingObj) {
        return {
          membershipId: obj2.membershipId,
          fundId: obj2.fundId,
          investmentId: obj2.investmentId,
          fundPercentage1: matchingObj.fundPercentage,
          fundPercentage2: obj2.fundPercentage,
          fundType: obj2.fundType,
          fundName: obj2.fundName,
          balance1: matchingObj.balance,
          balance2: obj2.balance,
          sellUnitPrice: obj2.sellUnitPrice,
          managedBy: obj2.managedBy,
          fundCharge: obj2.fundCharge,
          riskRating: obj2.riskRating,
          ESGRating: obj2.ESGRating,
        };
      } else {
        return {
          membershipId:
            obj2.membershipId !== undefined ? obj2.membershipId : null,
          fundId: obj2.fundId !== undefined ? obj2.fundId : null,
          investmentId: obj2.investmentId !== null ? obj2.investmentId : null,
          fundPercentage1: 0,
          fundPercentage2: obj2.fundPercentage,
          fundType: obj2.fundType,
          fundName: obj2.fundName,
          balance1:
            matchingObj?.balance !== undefined ? matchingObj.balance : 0,
          balance2: obj2.balance,
          sellUnitPrice: obj2.sellUnitPrice,
          managedBy: obj2.managedBy,
          fundCharge: obj2.fundCharge,
          riskRating: obj2.riskRating,
          ESGRating: obj2.ESGRating,
        };
      }
    });
    setFinalRequest(
      combinedArray.filter((fund) => {
        const lowerCaseFundName = fund.fundName.toLowerCase();
        return !(
          (lowerCaseFundName.includes("cash") &&
            lowerCaseFundName !== "cash fund" &&
            lowerCaseFundName !== "sipp tpt cash fund" &&
            lowerCaseFundName !== "tpt cash fund") ||
          lowerCaseFundName === "pending trades"
        );
      }),
    );
    setShowSummaryPopup(true);
  };

  const handleSummaryContinue = () => {
    popupShown.current = true;
    if (props.investmentStrategyKey === "currentInvestmentsAndFuturePayments") {
      handleCurrentAndFutureSubmit();
    } else if (props.investmentStrategyKey === "currentInvestmentsOnly") {
      handleCurrentSubmit();
    } else if (props.investmentStrategyKey === "futurePaymentsOnly") {
      handleFutureSubmit();
    }
  };

  const handleCurrentAndFutureSubmit = () => {
    let transformedData = {
      fromInvestments: [],
      toInvestments: [],
      strategyName: "currentInvestmentsAndFuturePayments",
    };
    finalRequest?.forEach((item) => {
      if (item.fundPercentage1 > item.fundPercentage2) {
        transformedData.fromInvestments.push({
          investmentId: item.investmentId,
          unitAmount: calculateUnits(
            item.balance1 - item.balance2,
            item.sellUnitPrice,
          ),
        });
      } else if (item.fundPercentage1 < item.fundPercentage2) {
        transformedData.toInvestments.push({
          investmentId: item.investmentId,
          percentageAmount: item.fundPercentage2 - item.fundPercentage1,
          amount: item.balance2,
        });
      }
    });
    transformedData = allocateRemainingPercentage(transformedData);
    savingsFundSwitch(
      transformedData,
      props.investorId,
      finalRequest[0]?.membershipId, // eslint-disable-line
    );
  };

  const handleCurrentSubmit = () => {
    let transformedData = {
      fromInvestments: [],
      toInvestments: [],
      strategyName: "currentInvestmentsOnly",
    };
    finalRequest?.forEach((item) => {
      if (item.fundPercentage1 > item.fundPercentage2) {
        transformedData.fromInvestments.push({
          investmentId: item.investmentId,
          unitAmount: calculateUnits(
            item.balance1 - item.balance2,
            item.sellUnitPrice,
          ),
        });
      } else if (item.fundPercentage1 < item.fundPercentage2) {
        transformedData.toInvestments.push({
          investmentId: item.investmentId,
          percentageAmount: item.fundPercentage2 - item.fundPercentage1,
          amount: item.balance2,
        });
      }
    });
    transformedData = allocateRemainingPercentage(transformedData);
    savingsFundSwitch(
      transformedData,
      props.investorId,
      finalRequest[0]?.membershipId, // eslint-disable-line
    );
  };

  const handleFutureSubmit = () => {
    const transformedData = {
      investorAccountId: finalRequest[0]?.membershipId,
      assetAllocation: [
        ...finalRequest.map((fund) => ({
          investmentId: fund.investmentId,
          investmentPercentage: fund.fundPercentage2.toString(),
        })),
      ],
    };
    savingsFundSwitchFuture(
      transformedData,
      props.investorId,
      finalRequest[0]?.membershipId, // eslint-disable-line
    );
  };

  useEffect(() => {
    if (
      fundSwitchFetching === false &&
      fundSwitchData.length > 0 &&
      fundSwitchData.every((obj) => obj.isSuccess) === true &&
      popupShown.current === true
    ) {
      if (
        props.investmentStrategyKey === "currentInvestmentsAndFuturePayments"
      ) {
        handleFutureSubmit();
      } else {
        setShowSummaryPopup(false);
        setChangesConfirmPopup(true);
        errorShown.current = false;
      }
    } else if (popupShown.current === true) {
      errorShown.current = true;
    }
  }, [fundSwitchFetching, fundSwitchData, fundSwitchError]);

  useEffect(() => {
    if (
      fundSwitchFutureFetching === false &&
      fundSwitchFutureData.isSuccess === true &&
      popupShown.current === true
    ) {
      setShowSummaryPopup(false);
      setChangesConfirmPopup(true);
      errorShown.current = false;
    } else if (popupShown.current === true) {
      errorShown.current = true;
    }
  }, [fundSwitchFutureFetching, fundSwitchFutureData, fundSwitchFutureError]);

  const handleConfirm = () => {
    popupShown.current = false;
    let combinedState;
    if (props.investmentStrategyKey === "currentInvestmentsOnly") {
      let investmentStrategy = "Future Payments Only";
      let investmentStrategyKey = "futurePaymentsOnly";
      combinedState = {
        investmentStrategy,
        investmentStrategyKey,
      };
    }
    navigate("/manage?tab=investments", { state: combinedState });
  };
  const date = new Date(memberships?.correctDate);
  const formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(
    date.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${date.getFullYear().toString()}`;
  return (
    <div className="mi-parent">
      <Row className="eac-back-button mb-2 mt-3">
        <Col lg={12} md={12} sm={12} xs={12}>
          <Link className="eac-back-link" to="#" onClick={handleBack}>
            <Icons iconname="chevron-left" variant="icon-sixteen-px" />
            Back
          </Link>
        </Col>
      </Row>
      <Card className="mi-card mb-4">
        {!addAnotherFund ? (
          <div>
            <div className="d-none d-xl-block d-lg-block d-md-block">
              <Row>
                {props.investmentStrategyKey !== "futurePaymentsOnly" ? (
                  <Col xl={3} lg={3} md={3} className="doughnut-chart-small">
                    <div>
                      <DoughnutChart
                        percentages={funds
                          .filter(
                            (fund) =>
                              fund.fundName !== "TPT Money Market Fund" &&
                              fund.fundName !== "SIPP TPT Money Market Fund", // eslint-disable-line
                          )
                          .map((fund) => fund.fundPercentage)}
                        colorPallete={funds
                          .filter(
                            (fund) =>
                              fund.fundName !== "TPT Money Market Fund" &&
                              fund.fundName !== "SIPP TPT Money Market Fund", // eslint-disable-line
                          )
                          .map((fund) => fund.color)}
                        showValue={false}
                      />
                    </div>
                  </Col>
                ) : (
                  ""
                )}
                <Col xl={9} lg={9} md={9} className="eac-pots-details">
                  <div
                    className={
                      props.investmentStrategyKey !== "futurePaymentsOnly"
                        ? "eac-pot-name"
                        : "eac-pot-name-future"
                    }
                  >
                    {memberships?.memberships?.length > 1
                      ? props.memberships?.memberships?.some(
                          (membership) =>
                            membership.funds.some(
                              (fund) =>
                                fund.fundName === "TPT Money Market Fund" ||
                                fund.fundName === "SIPP TPT Money Market Fund", // eslint-disable-line
                            ), // eslint-disable-line
                        ) !== false
                        ? "Total savings pot (excluding GP3 fund)"
                        : "Total value"
                      : props.memberships?.memberships?.some(
                            (membership) =>
                              membership.funds.some(
                                (fund) =>
                                  fund.fundName === "TPT Money Market Fund" ||
                                  fund.fundName === "SIPP TPT Money Market Fund", // eslint-disable-line
                              ), // eslint-disable-line
                          ) !== false
                        ? memberships?.memberships[0]?.membershipName +
                          " (excluding GP3 fund)"
                        : memberships?.memberships[0]?.membershipName}
                  </div>
                  {props.investmentStrategyKey !== "futurePaymentsOnly" ? (
                    <>
                      <div className="eac-pot-value">
                        £{addCommas(Math.trunc(memberships?.totalFund))}.
                        <span className="eac-pot-value-small-digit">
                          {(memberships?.totalFund % 1).toFixed(2).substring(2)}
                        </span>
                      </div>
                      <div className="eac-last-checked-date">
                        As of {formattedDate}
                      </div>{" "}
                    </>
                  ) : (
                    ""
                  )}
                  <div
                    className={
                      props.investmentStrategyKey !== "futurePaymentsOnly"
                        ? "eac-curr-invest-text"
                        : "eac-curr-invest-text-future"
                    }
                  >
                    {props.investmentStrategy}
                  </div>
                </Col>
              </Row>
              <hr className="narrow-hr" />
              <div className="fund-desc-parent">
                <Row>
                  <Col xl={8} lg={8} md={8} className="fund-desc">
                    {props.investmentStrategyKey === "futurePaymentsOnly"
                      ? "These are your selected funds. Use the sliders to adjust your funds or click on the 'add another funds' button to view more investment choices."
                      : "These are your current investments and selected funds. Use the sliders to adjust your existing funds or click on the 'add another fund' button to view more investment choices."}
                  </Col>
                  <Col
                    xl={4}
                    lg={4}
                    md={4}
                    className="fund-desc-button-saving text-end"
                  >
                    <Buttons
                      name={
                        funds.some((fund) => fund.checked === true)
                          ? "Edit funds"
                          : "Add another fund"
                      }
                      className="primary"
                      iconname={isHovered ? "plus" : "plus-white"}
                      variantname="icon-sixteen-px"
                      handleClick={handleAddAnotherFund}
                      iconPosition="left"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    ></Buttons>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="eac-mobile-container d-block d-sm-none">
              <Card className="mobile-card">
                {props.investmentStrategyKey !== "futurePaymentsOnly" ? (
                  <Row>
                    <Col xl={3} lg={3} md={3} className="mobile-dc-col">
                      <div className="mobile-doughnut-chart">
                        <DoughnutChart
                          percentages={funds
                            .filter(
                              (membership) =>
                                membership.fundName !==
                                  "TPT Money Market Fund" &&
                                membership.fundName !==
                                  "SIPP TPT Money Market Fund", // eslint-disable-line
                            )
                            .map((membership) => membership.fundPercentage)}
                          colorPallete={funds
                            .filter(
                              (membership) =>
                                membership.fundName !==
                                  "TPT Money Market Fund" &&
                                membership.fundName !==
                                  "SIPP TPT Money Market Fund", // eslint-disable-line
                            )
                            .map((membership) => membership.color)}
                          showValue={false}
                        />
                      </div>
                    </Col>
                  </Row>
                ) : (
                  ""
                )}
                <Row
                  className={
                    props.investmentStrategyKey !== "futurePaymentsOnly"
                      ? "eac-mobile-total-value"
                      : "eac-mobile-total-value-future"
                  }
                >
                  <div>
                    {memberships?.memberships?.length > 1
                      ? "Total value"
                      : memberships?.memberships[0]?.membershipName}
                  </div>
                </Row>
                {props.investmentStrategyKey !== "futurePaymentsOnly" ? (
                  <div className="eac-mobile-total-fund-saving">
                    £{addCommas(Math.trunc(memberships?.totalFund))}.
                    <span className="eac-mobile-small-digit">
                      {(memberships?.totalFund % 1).toFixed(2).substring(2)}
                    </span>
                  </div>
                ) : (
                  ""
                )}
                <Row className="eac-mobile-strategy">
                  <div>{props.investmentStrategy}</div>
                </Row>
                <hr className="mobile-hr" />
                <Row className="eac-mobile-fund-dec mb-3">
                  <Col xl={8} lg={8} md={8}>
                    {props.investmentStrategyKey === "futurePaymentsOnly"
                      ? "These are your selected funds. Use the sliders to adjust your funds or click on the 'add another funds' button to view more investment choices."
                      : "These are your current investments and selected funds. Use the sliders to adjust your existing funds or click on the 'add another fund' button to view more investment choices."}
                  </Col>
                </Row>
                <Row className="eac-add-button mb-3">
                  <Col xl={4} lg={4} md={4}>
                    <Buttons
                      name="Add another fund"
                      className="primary"
                      iconname={isHovered ? "plus" : "plus-white"}
                      variantname="icon-sixteen-px"
                      handleClick={handleAddAnotherFund}
                      iconPosition="left"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    ></Buttons>
                  </Col>
                </Row>
              </Card>
            </div>
            <ExploreAndChangeInvestment
              category="Managed for me"
              data={funds}
              totalFund={memberships?.totalFund}
              onSliderChange={handleOnSliderChange}
              investmentStrategyKey={props.investmentStrategyKey}
            />
            <ExploreAndChangeInvestment
              category="Self-managed"
              data={funds}
              totalFund={memberships?.totalFund}
              onSliderChange={handleOnSliderChange}
              investmentStrategyKey={props.investmentStrategyKey}
            />
          </div>
        ) : (
          <div>
            <div className="another-fund-desc">
              Please select from the following investment choices. The next step
              will be to allocate the proportion in each fund.
            </div>
            <FundSelect
              category="Managed for me"
              data={selectFund}
              onCardSelect={handleCheckboxChange}
            />
            <FundSelect
              category="Self-managed"
              data={selectFund}
              onCardSelect={handleCheckboxChange}
            />
          </div>
        )}
      </Card>

      {!addAnotherFund ? (
        <Card className="footer-card">
          <div className="footer-button">
            <div>
              <Buttons
                name="Cancel"
                className="secondary cancle-button"
                variantname="icon-sixteen-px"
                handleClick={handleCancel}
              ></Buttons>
            </div>
            <div>
              <span className="funds-selected-saving">
                Allocated:{" "}
                {isNaN(
                  funds
                    ?.reduce(
                      (acc, fund) =>
                        fund.fundName === "TPT Money Market Fund" ||
                        fund.fundName === "SIPP TPT Money Market Fund"
                          ? acc
                          : acc + parseFloat(fund.fundPercentage),
                      0, // eslint-disable-line
                    )
                    .toFixed(2), // eslint-disable-line
                )
                  ? 0
                  : funds
                      ?.reduce(
                        (acc, fund) =>
                          fund.fundName === "TPT Money Market Fund" ||
                          fund.fundName === "SIPP TPT Money Market Fund"
                            ? acc
                            : acc + parseFloat(fund.fundPercentage),
                        0, // eslint-disable-line
                      )
                      .toFixed(2)}
                %
              </span>{" "}
              <Buttons
                name="Continue"
                className="primary continue-button"
                variantname="icon-sixteen-px"
                disabled={
                  funds
                    .reduce(
                      (acc, fund) =>
                        fund.fundName === "TPT Money Market Fund" ||
                        fund.fundName === "SIPP TPT Money Market Fund"
                          ? acc
                          : acc + parseFloat(fund.fundPercentage),
                      0 // eslint-disable-line
                    )
                    ?.toFixed(2) != 100 || !checkIsDirty
                }
                handleClick={handleFinalSubmit}
              ></Buttons>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="footer-card">
          <div className="footer-button">
            <div>
              <Buttons
                name="Cancel"
                className="secondary"
                variantname="icon-sixteen-px"
                handleClick={handleCancel}
              ></Buttons>
            </div>
            <div>
              <span className="funds-selected-saving">
                {selectFund?.filter((obj) => obj?.checked).length} funds
                selected
              </span>
              <Buttons
                name="Continue"
                className="primary"
                variantname="icon-sixteen-px"
                handleClick={handleContinue}
                disabled={
                  selectFund?.filter((obj) => obj?.checked).length > 0
                    ? false
                    : true
                }
              ></Buttons>
            </div>
          </div>
        </Card>
      )}

      <ModalOverlay
        popup="summaryOfChanges"
        show={showSummaryPopup}
        onHide={(e) => {
          if (e !== undefined) {
            e.preventDefault();
          }
          popupShown.current === false;
          setShowSummaryPopup(false);
        }}
        modaltitle="Summary of changes"
        finalFund={finalRequest}
        handleContinue={handleSummaryContinue}
        investmentStrategy={props.investmentStrategy}
        investmentStrategyKey={props.investmentStrategyKey}
        memberships={memberships}
        fundSwitchFetching={fundSwitchFetching}
        fundSwitchFutureFetching={fundSwitchFutureFetching}
        fundSwitchError={fundSwitchError}
        fundSwitchFutureError={fundSwitchFutureError}
        errorShown={errorShown.current}
      />
      <ModalOverlay
        popup="changesConfirmed"
        show={changesConfirmPopup}
        investmentStrategyKey={props.investmentStrategyKey}
        onHide={(e) => {
          if (e !== undefined) {
            e.preventDefault();
          }
          popupShown.current === false;
          setChangesConfirmPopup(false);
        }}
        modaltitle="Changes confirmed"
        handleContinue={handleConfirm}
        disableBackdropClose={true}
        disableEscClose={true}
      />
    </div>
  );
};

export default ManageInvestment;
